/**
 * Map POIs (Points of Interest) Management
 * 
 * This module handles the creation, management, and styling of POIs on Mapbox maps.
 * It includes SVG recoloring functionality for accessibility icons to match theme colors.
 * 
 * Features:
 * - Automatic SVG recoloring based on theme colors
 * - Support for custom icon colors per POI
 * - Clustering for better performance
 * - Accessibility icon support
 * 
 * Usage:
 * 
 * // Basic POI with theme colors
 * const pois = [{
 *   title: "Accessible Entrance",
 *   description: "Main accessible entrance",
 *   location: [longitude, latitude],
 *   icon: "accessibility-1"
 * }];
 * 
 * // POI with custom icon color
 * const pois = [{
 *   title: "Special Access",
 *   description: "Special access point",
 *   location: [longitude, latitude],
 *   icon: "accessibility-2",
 *   bgColor: "#ff0000"  // Custom red color
 * }];
 * 
 * await addPOIs({ map, pois, bgColor, textColor, setTooltip, scenes, slug });
 */
import mapboxgl from 'mapbox-gl';
import PopupMap from "./PopupMap.svelte";
import { recolorSvg, createDataUri } from "./svgRecolor.js";
import { getDirectionsCoordsForPopup } from "./mapDirectionsHelpers.js";
import {
  normalizeNumericPoiMarkerStyle,
  createNumericMapboxMarkerElement,
  applyNumericDomMarkerHover,
  getNumericPoiTooltipOffsetYFromMarker,
  numericMarkerHoverScale,
  runDuringNumericMarkerScaleTransition,
} from "./numericPoiMarker.js";

function getBlackOrWhite(color) {
  return parseInt(color.replace("#", ""), 16) > 0xffffff / 2
    ? "#000000"
    : "#ffffff";
}

/** Touch-primary viewports: use touchend + preventDefault instead of click to avoid ghost clicks. */
function prefersTouchTap() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return (
    window.matchMedia("(pointer: coarse)").matches ||
    window.matchMedia("(hover: none)").matches
  );
}

function getPopup(poi, scenes, mapDisplayMode) {
  if (!poi) return;
  const { title, description } = poi;
  let { sceneIds } = poi;
  // Parse JSON strings
  if (typeof sceneIds === "string") sceneIds = JSON.parse(sceneIds);

  // convert the scene ids into linked scenes
  const linkedscenes = sceneIds?.map((sceneId) => {
    const scene = scenes?.find((scene) => scene.id === sceneId);
    return {
      id: sceneId,
      name: scene?.name,
      title: scene?.title,
    };
  });

  const directions = getDirectionsCoordsForPopup(poi, mapDisplayMode);

  return {
    title,
    content: description,
    linkedscenes,
    directions,
  };
}

// get a darker or lighter color for hover effect (depending on darkness of color)
function getHoverColor(color) {
  const colorInt = parseInt(color.replace("#", ""), 16);
  const isDark = colorInt > 0xffffff / 2;
  const factor = isDark ? 0.9 : 1.1;
  const newColorInt = Math.round(colorInt * factor);
  return "#" + newColorInt.toString(16);
}

/** Stable feature id for a POI so menu selection matches map (POIs without config id get id from title+location). */
function getPoiFeatureId(poi, index) {
  if (poi.id != null && poi.id !== "") return String(poi.id);
  let loc = "";
  if (Array.isArray(poi.location) && poi.location.length >= 2) {
    loc = poi.location.join(",");
  } else if (typeof poi.location === "string") {
    try {
      const parsed = JSON.parse(poi.location);
      loc = Array.isArray(parsed) && parsed.length >= 2 ? parsed.join(",") : String(poi.location).replace(/\s/g, "");
    } catch {
      loc = String(poi.location).replace(/\s/g, "");
    }
  }
  const title = (poi.title || "").replace(/\s/g, "");
  return title || loc ? `poi-${title}-${loc}` : `poi-${index}`;
}

/**
 * Sanitize Iconify id for use in Mapbox layer/image ids (e.g. "mdi:map-marker" -> "mdi-map-marker")
 */
function sanitizeIconifyIdForLayer(iconifyId) {
  return iconifyId.replace(/:/g, "-");
}

/**
 * Load an Iconify icon with color from API (api.iconify.design/prefix/name.svg?color=%23hex)
 * @param {string} iconifyId - e.g. "mdi:map-marker"
 * @param {string} color - hex color with or without #
 * @returns {Promise<HTMLImageElement>}
 */
async function loadIconifyIcon(iconifyId, color) {
  const [prefix, name] = iconifyId.split(":");
  if (!prefix || !name) throw new Error(`Invalid Iconify id: ${iconifyId}`);
  const hex = color.replace("#", "");
  const url = `https://api.iconify.design/${prefix}/${name}.svg?color=%23${hex}`;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load Iconify icon: ${iconifyId}`));
    img.src = url;
  });
}

/**
 * Load and recolor an SVG icon for use in Mapbox
 * @param {string} iconPath - Path to the SVG file
 * @param {string} color - Color to apply to the SVG
 * @returns {Promise<HTMLImageElement>} Promise that resolves to an Image element
 */
async function loadRecoloredIcon(iconPath, color) {
  try {
    const response = await fetch(iconPath, {
      mode: 'cors',
      credentials: 'omit'
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const svgText = await response.text();
    
    // Recolor the SVG
    const recoloredSvg = recolorSvg(svgText, color);
    const dataUri = createDataUri(recoloredSvg);
    
    // Create and return an Image element
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUri;
    });
  } catch (error) {
    console.error('Failed to load or recolor icon:', error);
    throw error;
  }
}

const LEGACY_SYMBOL_ICONS = ['accessibility-1', 'accessibility-2', 'accessibility-3', 'accessibility-4', 'pin'];

export function removePois({ map }) {
  console.log("removePois");
  const symbolIconIds = map._poiSymbolIconIds ?? LEGACY_SYMBOL_ICONS;

  if (map._poiListeners) {
    Object.entries(map._poiListeners.clusters).forEach(([event, handler]) => {
      map.off(event, "clusters", handler);
    });
    Object.entries(map._poiListeners.unclusteredPointDefault).forEach(([event, handler]) => {
      map.off(event, "unclustered-point-default", handler);
    });
    symbolIconIds.forEach((iconName) => {
      const listenerKey = `unclusteredPoint${iconName.charAt(0).toUpperCase() + iconName.slice(1).replace(/-/g, "")}`;
      if (map._poiListeners[listenerKey]) {
        Object.entries(map._poiListeners[listenerKey]).forEach(([event, handler]) => {
          map.off(event, `unclustered-point-${iconName}`, handler);
        });
      }
    });
    delete map._poiListeners;
  }

  const symbolLayers = symbolIconIds.map((icon) => `unclustered-point-${icon}`);
  const poiLayers = ['clusters', 'cluster-count', 'unclustered-point-default', ...symbolLayers];
  poiLayers.forEach((layer) => {
    if (map.getLayer(layer)) {
      map.removeLayer(layer);
    }
  });

  delete map._poiSymbolIconIds;
  delete map._poiInitialGeojson;
  delete map._poiHiddenPoiId;
  delete map._poiHiddenFeature;

  if (map.getSource('pois')) {
    map.removeSource('pois');
  }
}

export async function addPOIs({ map, pois, bgColor, textColor, hoverColor: hoverColorProp, setTooltip, scenes, slug, onPoiClick, mapDisplayMode }) {
  // Keep full list for icon/layer discovery (so hidden POIs' icons get layers too)
  const allPoisForIcons = pois;
  // filter out hidden pois and sort by Map Menu order, then by name
  pois = pois.filter((poi) => !poi.hidden).sort((a, b) => {
    const oA = a.order ?? 0, oB = b.order ?? 0;
    if (oA !== oB) return oA - oB;
    return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
  });
  // setup colors
  bgColor = bgColor || "#000000";
  textColor = textColor || getBlackOrWhite(bgColor);
  const strokeColor = textColor;
  const hoverColor = (hoverColorProp && hoverColorProp.trim()) ? hoverColorProp : getHoverColor(bgColor);
  // just use the same color as the points for now
  const clusterColor = bgColor;
  // determine if cluster text color should be white or black based on darkness of cluster color
  const clusterTextColor = textColor;

  // Symbol icons (accessibility + pin) that use SVG layers
  const symbolIcons = ['accessibility-1', 'accessibility-2', 'accessibility-3', 'accessibility-4', 'pin'];

  // Get unique symbol icons used by any POI (including hidden) so layers exist when hidden POI is shown
  const usedIcons = [...new Set(allPoisForIcons
    .filter(poi => poi.icon && symbolIcons.includes(poi.icon))
    .map(poi => poi.icon)
  )];

  console.log(`Loading ${usedIcons.length} symbol icons:`, usedIcons);

  // Load and recolor only the symbol icons that are actually used
  for (const iconName of usedIcons) {
    if (!map.hasImage(`${iconName}-icon`)) {
      try {
        const iconPath = `./images/accessibility/${iconName}.svg`;
        const img = await loadRecoloredIcon(iconPath, bgColor);
        img.width = 100;
        img.height = 100;
        map.addImage(`${iconName}-icon`, img);
        console.log(`Loaded and recolored icon: ${iconName} with color ${bgColor}`);
      } catch (error) {
        console.error(`Failed to load icon ${iconName}:`, error);
        // Fallback to original loading method
        const img = new Image();
        img.width = 100;
        img.height = 100;
        img.onload = () => {
          map.addImage(`${iconName}-icon`, img);
        };
        img.src = `./images/accessibility/${iconName}.svg`;
      }
    }
  }

  // Load custom colored icons for POIs that specify custom colors (include hidden so icon exists)
  const customColorPOIs = allPoisForIcons.filter(poi => poi.icon && symbolIcons.includes(poi.icon) && poi.bgColor);
  for (const poi of customColorPOIs) {
    const iconName = poi.icon;
    const customIconId = `${iconName}-${poi.bgColor.replace('#', '')}-icon`;
    
    if (!map.hasImage(customIconId)) {
      try {
        const iconPath = `./images/accessibility/${iconName}.svg`;
        const img = await loadRecoloredIcon(iconPath, poi.bgColor);
        img.width = 100;
        img.height = 100;
        map.addImage(customIconId, img);
        console.log(`Loaded custom colored icon: ${iconName} with color ${poi.bgColor}`);
      } catch (error) {
        console.error(`Failed to load custom icon ${iconName}:`, error);
      }
    }
  }

  // Load hover-colored SVG icons so we can switch icon via setData (include hidden so hover icon exists)
  const symbolPois = allPoisForIcons.filter(poi => poi.icon && symbolIcons.includes(poi.icon));
  const hoverColorPairs = new Set();
  symbolPois.forEach(poi => {
    const effectiveHover = (poi.hoverColor && poi.hoverColor.trim()) ? poi.hoverColor : hoverColor;
    hoverColorPairs.add(`${poi.icon}\t${effectiveHover}`);
  });
  for (const pair of hoverColorPairs) {
    const [iconName, color] = pair.split("\t");
    const hoverIconId = `${iconName}-${color.replace("#", "")}-hover-icon`;
    if (!map.hasImage(hoverIconId)) {
      try {
        const iconPath = `./images/accessibility/${iconName}.svg`;
        const img = await loadRecoloredIcon(iconPath, color);
        img.width = 100;
        img.height = 100;
        map.addImage(hoverIconId, img);
      } catch (error) {
        console.error(`Failed to load hover icon ${iconName}:`, error);
      }
    }
  }

  // Iconify icons: collect used ids from any POI (including hidden) so layers exist when hidden POI is shown
  const usedIconifyIds = [...new Set(allPoisForIcons.filter((poi) => poi.icon && String(poi.icon).includes(":")).map((poi) => poi.icon))];
  const iconifyColorPairs = [];
  usedIconifyIds.forEach((iconifyId) => {
    iconifyColorPairs.push({ iconifyId, color: bgColor });
  });
  allPoisForIcons.forEach((poi) => {
    const iconifyId = poi.icon && String(poi.icon).includes(":") ? poi.icon : null;
    const effectiveBg = (poi.bgColor && poi.bgColor.trim()) ? poi.bgColor : bgColor;
    if (iconifyId && effectiveBg !== bgColor && !iconifyColorPairs.some((p) => p.iconifyId === iconifyId && p.color === effectiveBg)) {
      iconifyColorPairs.push({ iconifyId, color: effectiveBg });
    }
  });
  for (const { iconifyId, color } of iconifyColorPairs) {
    const layerId = sanitizeIconifyIdForLayer(iconifyId);
    const hex = color.replace("#", "");
    const imageId = color === bgColor ? `${layerId}-icon` : `${layerId}-${hex}-icon`;
    if (map.hasImage(imageId)) continue;
    try {
      const img = await loadIconifyIcon(iconifyId, color);
      img.width = 100;
      img.height = 100;
      map.addImage(imageId, img);
    } catch (error) {
      console.warn(`Could not load Iconify icon ${iconifyId}, skipping.`, error);
    }
  }

  // Load hover-colored Iconify icons so we can switch icon on hover/selection (include hidden)
  const iconifyHoverColorPairs = [];
  usedIconifyIds.forEach((iconifyId) => {
    iconifyHoverColorPairs.push({ iconifyId, color: hoverColor });
  });
  allPoisForIcons.forEach((poi) => {
    const iconifyId = poi.icon && String(poi.icon).includes(":") ? poi.icon : null;
    const effectiveHover = (poi.hoverColor && poi.hoverColor.trim()) ? poi.hoverColor : hoverColor;
    if (iconifyId && !iconifyHoverColorPairs.some((p) => p.iconifyId === iconifyId && p.color === effectiveHover)) {
      iconifyHoverColorPairs.push({ iconifyId, color: effectiveHover });
    }
  });
  for (const { iconifyId, color } of iconifyHoverColorPairs) {
    const layerId = sanitizeIconifyIdForLayer(iconifyId);
    const hex = color.replace("#", "");
    const imageId = `${layerId}-${hex}-hover-icon`;
    if (map.hasImage(imageId)) continue;
    try {
      const img = await loadIconifyIcon(iconifyId, color);
      img.width = 100;
      img.height = 100;
      map.addImage(imageId, img);
    } catch (error) {
      console.warn(`Could not load Iconify hover icon ${iconifyId}, skipping.`, error);
    }
  }

  const geojsonSource = {
    type: "FeatureCollection",
    features: pois.map((poi, index) => {
      let coordinates;
      if (typeof poi.location === "string") {
        try {
          coordinates = JSON.parse(poi.location);
        } catch {
          return null;
        }
      } else if (Array.isArray(poi.location) && poi.location.length === 2) {
        coordinates = poi.location;
      } else {
        return null;
      }
      const effectiveHoverColor = (poi.hoverColor && poi.hoverColor.trim())
        ? poi.hoverColor
        : hoverColor;
      const featureId = getPoiFeatureId(poi, index);
      const isSymbol = poi.icon && symbolIcons.includes(poi.icon);
      const isIconify = poi.icon && String(poi.icon).includes(":");
      const effectiveBg = (poi.bgColor && poi.bgColor.trim()) ? poi.bgColor : bgColor;
      const iconImage = isSymbol
        ? (poi.bgColor ? `${poi.icon}-${poi.bgColor.replace("#", "")}-icon` : `${poi.icon}-icon`)
        : isIconify
          ? (effectiveBg === bgColor ? `${sanitizeIconifyIdForLayer(poi.icon)}-icon` : `${sanitizeIconifyIdForLayer(poi.icon)}-${effectiveBg.replace("#", "")}-icon`)
          : null;
      const hoverIconId = isSymbol
        ? `${poi.icon}-${effectiveHoverColor.replace("#", "")}-hover-icon`
        : isIconify
          ? `${sanitizeIconifyIdForLayer(poi.icon)}-${effectiveHoverColor.replace("#", "")}-hover-icon`
          : null;
      return {
        type: "Feature",
        id: featureId,
        properties: {
          ...poi,
          id: featureId,
          icon: poi.icon || "default",
          bgColor: effectiveBg,
          hoverColor: effectiveHoverColor,
          ...((isSymbol || isIconify) && { iconImage, hoverIconId, currentIcon: iconImage }),
        },
        geometry: {
          type: "Point",
          coordinates,
        },
      };
    }).filter(Boolean),
  };

  map.addSource("pois", {
    type: "geojson",
    data: geojsonSource,
    cluster: true,
    clusterRadius: 50,
    clusterMaxZoom: 14,
    promoteId: "id",
  });

  map._poiInitialGeojson = JSON.parse(JSON.stringify(geojsonSource));

  map.addLayer({
    id: "clusters",
    type: "circle",
    source: "pois",
    filter: ["has", "point_count"],
    paint: {
      "circle-color": clusterColor,
      "circle-radius": 24,
      "circle-stroke-width": 2,
      "circle-stroke-color": clusterTextColor,
    },
  });

  map.addLayer({
    id: "cluster-count",
    type: "symbol",
    source: "pois",
    filter: ["has", "point_count"],
    layout: {
      "text-field": "{point_count_abbreviated}",
      "text-font": ["Arial Unicode MS Bold", "DIN Offc Pro Bold"],
      "text-size": 12,
    },
    paint: {
      "text-color": clusterTextColor,
    },
  });

  // Add default circle POIs (with hover via feature-state; hoverColor per feature)
  map.addLayer({
    id: "unclustered-point-default",
    type: "circle",
    source: "pois",
    filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "icon"], "default"]],
    paint: {
      "circle-color": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        ["get", "hoverColor"],
        ["get", "bgColor"],
      ],
      "circle-radius": ["*", 0.5, ["to-number", ["coalesce", ["get", "iconSize"], 20]]],
      "circle-stroke-width": 2,
      "circle-stroke-color": strokeColor,
    },
  });

  /** Scale symbol relative to ~28px design baseline (matches Leaflet / CMS). */
  const iconSizeScaleExpr = ["*", 0.3, ["/", ["to-number", ["coalesce", ["get", "iconSize"], 28]], 28]];

  // Symbol POIs: icon-image from currentIcon so we can switch to hover color via setData (no feature-state in layout)
  usedIcons.forEach(iconName => {
    map.addLayer({
      id: `unclustered-point-${iconName}`,
      type: "symbol",
      source: "pois",
      filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "icon"], iconName]],
      layout: {
        "icon-image": ["get", "currentIcon"],
        "icon-size": iconSizeScaleExpr,
        "icon-allow-overlap": true,
      },
    });
  });

  // Iconify symbol layers (icon-image from currentIcon so we can switch to hover via setData)
  usedIconifyIds.forEach((iconifyId) => {
    const layerId = sanitizeIconifyIdForLayer(iconifyId);
    map.addLayer({
      id: `unclustered-point-${layerId}`,
      type: "symbol",
      source: "pois",
      filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "icon"], iconifyId]],
      layout: {
        "icon-image": ["get", "currentIcon"],
        "icon-size": iconSizeScaleExpr,
        "icon-allow-overlap": true,
      },
    });
  });

  let hoveredPoiId = null;

  function getPointFromTouch(touchEvent) {
    if (!touchEvent.changedTouches?.length) return null;
    const t = touchEvent.changedTouches[0];
    const canvas = map.getCanvas();
    const rect = canvas.getBoundingClientRect();
    const x = t.clientX - rect.left;
    const y = t.clientY - rect.top;
    if (typeof x !== "number" || typeof y !== "number" || Number.isNaN(x) || Number.isNaN(y)) return null;
    return { x, y };
  }

  // Store event listeners for cleanup
  const listeners = {
    clusters: {
      click: (e) => {
        if (map._mapboxTouchHandledUntil && Date.now() < map._mapboxTouchHandledUntil) return;
        const features = map.queryRenderedFeatures(e.point, {
          layers: ["clusters"],
        });
        if (!features?.length) return;
        const clusterId = features[0].properties.cluster_id;
        const clusterSource = map.getSource("pois");

        clusterSource.getClusterLeaves(
          clusterId,
          Infinity,
          0,
          (error, points) => {
            if (error) return;
            const coordinates = points.map((point) => point.geometry.coordinates);
            const bounds = coordinates.reduce(
              (bounds, coord) => bounds.extend(coord),
              new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
            );
            map.fitBounds(bounds, {
              padding: 100,
              maxZoom: 17,
            });
          },
        );
      },
      mouseenter: () => {
        map.getCanvas().style.cursor = "pointer";
      },
      mousemove: (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ["clusters"],
        });
        if (features.length) {
          map.getCanvas().style.cursor = "pointer";
        } else {
          map.getCanvas().style.cursor = "";
        }
      },
      mouseleave: () => {
        map.getCanvas().style.cursor = "";
      }
    },
    unclusteredPointDefault: {
      click: (e) => {
        if (map._mapboxTouchHandledUntil && Date.now() < map._mapboxTouchHandledUntil) return;
        const props = e.features?.[0]?.properties;
        if (onPoiClick && props) {
          onPoiClick(props);
        } else {
          const popup = getPopup(props, scenes, mapDisplayMode);
          if (popup) {
            new PopupMap({ target: document.body, props: popup });
          }
        }
      },
      mousemove: (e) => {
        const x = e.point.x;
        const y = e.point.y;
        const text = e.features[0]?.properties?.title;
        if (text) {
          map._nonBuildingTooltipActive = true;
          setTooltip({ x, y, text });
        }

        const popup = getPopup(e.features?.[0]?.properties, scenes, mapDisplayMode);
        if (popup) {
          map.getCanvas().style.cursor = "pointer";
        }
        if (e.features.length > 0) {
          const featureId = e.features[0].id ?? e.features[0].properties?.id;
          if (featureId && featureId !== hoveredPoiId) {
            if (hoveredPoiId && hoveredPoiId !== selectedPoiId) {
              try {
                map.setFeatureState({ source: "pois", id: hoveredPoiId }, { hover: false });
              } catch (_) {}
            }
            hoveredPoiId = featureId;
            try {
              map.setFeatureState({ source: "pois", id: featureId }, { hover: true });
            } catch (_) {}
          }
        }
      },
      mouseleave: () => {
        if (hoveredPoiId) {
          if (hoveredPoiId !== selectedPoiId) {
            try {
              map.setFeatureState({ source: "pois", id: hoveredPoiId }, { hover: false });
            } catch (_) {}
          }
          hoveredPoiId = null;
        }
        map._nonBuildingTooltipActive = false;
        map.getCanvas().style.cursor = "";
        setTooltip(null);
      }
    },
    // Accessibility icon event listeners will be added dynamically
  };

  // Find a feature in geojsonSource by id (for updating currentIcon on hover/selection)
  function findPoiFeature(id) {
    return geojsonSource.features.find((f) => f.id === id || f.properties?.id === id);
  }

  let selectedPoiId = null;

  /** Apply hover color to symbol POIs that are either hovered or selected (map menu). */
  function applySymbolHighlight() {
    geojsonSource.features.forEach((f) => {
      if (f.properties?.hoverIconId) {
        const isHighlighted = f.id === selectedPoiId || f.id === hoveredPoiId;
        f.properties.currentIcon = isHighlighted ? f.properties.hoverIconId : f.properties.iconImage;
      }
    });
    // When hidden POI is selected/hovered, show it in hover color (symbol/iconify only)
    if (map._poiHiddenFeature?.properties?.hoverIconId) {
      const hiddenId = map._poiHiddenFeature.id ?? map._poiHiddenFeature.properties?.id;
      const isHighlighted = hiddenId === selectedPoiId || hiddenId === hoveredPoiId;
      map._poiHiddenFeature.properties.currentIcon = isHighlighted
        ? map._poiHiddenFeature.properties.hoverIconId
        : map._poiHiddenFeature.properties.iconImage;
    }
    let dataToSet = geojsonSource;
    if (map._poiHiddenFeature) {
      dataToSet = {
        type: "FeatureCollection",
        features: [...geojsonSource.features, map._poiHiddenFeature],
      };
    }
    map.getSource("pois").setData(dataToSet);
  }

  map.highlightPoi = function (poiOrId) {
    const poiId =
      poiOrId != null && typeof poiOrId === "object"
        ? getPoiFeatureId(poiOrId, 0)
        : poiOrId != null
          ? String(poiOrId)
          : null;
    if (selectedPoiId) {
      try {
        map.setFeatureState({ source: "pois", id: selectedPoiId }, { hover: false });
      } catch (_) {}
    }
    selectedPoiId = poiId || null;
    if (selectedPoiId) {
      try {
        map.setFeatureState({ source: "pois", id: selectedPoiId }, { hover: true });
      } catch (_) {}
    }
    applySymbolHighlight();
  };

  map.clearPoiHighlight = function () {
    if (selectedPoiId) {
      try {
        map.setFeatureState({ source: "pois", id: selectedPoiId }, { hover: false });
      } catch (_) {}
      selectedPoiId = null;
    }
    applySymbolHighlight();
  };

  usedIcons.forEach(iconName => {
    listeners[`unclusteredPoint${iconName.charAt(0).toUpperCase() + iconName.slice(1).replace(/-/g, "")}`] = {
      click: (e) => {
        if (map._mapboxTouchHandledUntil && Date.now() < map._mapboxTouchHandledUntil) return;
        const props = e.features?.[0]?.properties;
        if (onPoiClick && props) {
          onPoiClick(props);
        } else {
          const popup = getPopup(props, scenes, mapDisplayMode);
          if (popup) {
            new PopupMap({ target: document.body, props: popup });
          }
        }
      },
      mousemove: (e) => {
        const x = e.point.x;
        const y = e.point.y;
        const text = e.features[0]?.properties?.title;
        if (text) {
          map._nonBuildingTooltipActive = true;
          setTooltip({ x, y, text });
        }

        const popup = getPopup(e.features?.[0]?.properties, scenes, mapDisplayMode);
        if (popup) {
          map.getCanvas().style.cursor = "pointer";
        }
        if (e.features.length > 0) {
          const featureId = e.features[0].id ?? e.features[0].properties?.id;
          if (featureId && featureId !== hoveredPoiId) {
            hoveredPoiId = featureId;
            applySymbolHighlight();
          }
        }
      },
      mouseleave: () => {
        if (hoveredPoiId) {
          hoveredPoiId = null;
          applySymbolHighlight();
        }
        map._nonBuildingTooltipActive = false;
        map.getCanvas().style.cursor = "";
        setTooltip(null);
      }
    };
  });

  usedIconifyIds.forEach((iconifyId) => {
    const layerId = sanitizeIconifyIdForLayer(iconifyId);
    const listenerKey = `unclusteredPoint${layerId.charAt(0).toUpperCase() + layerId.slice(1).replace(/-/g, "")}`;
    listeners[listenerKey] = {
      click: (e) => {
        if (map._mapboxTouchHandledUntil && Date.now() < map._mapboxTouchHandledUntil) return;
        const props = e.features?.[0]?.properties;
        if (onPoiClick && props) {
          onPoiClick(props);
        } else {
          const popup = getPopup(props, scenes, mapDisplayMode);
          if (popup) {
            new PopupMap({ target: document.body, props: popup });
          }
        }
      },
      mousemove: (e) => {
        const text = e.features[0]?.properties?.title;
        if (text) {
          map._nonBuildingTooltipActive = true;
          setTooltip({ x: e.point.x, y: e.point.y, text });
        }
        map.getCanvas().style.cursor = "pointer";
        if (e.features?.length > 0) {
          const featureId = e.features[0].id ?? e.features[0].properties?.id;
          if (featureId && featureId !== hoveredPoiId) {
            hoveredPoiId = featureId;
            applySymbolHighlight();
          }
        }
      },
      mouseleave: () => {
        if (hoveredPoiId) {
          hoveredPoiId = null;
          applySymbolHighlight();
        }
        map._nonBuildingTooltipActive = false;
        map.getCanvas().style.cursor = "";
        setTooltip(null);
      }
    };
  });

  // Add event listeners
  Object.entries(listeners.clusters).forEach(([event, handler]) => {
    map.on(event, "clusters", handler);
  });

  Object.entries(listeners.unclusteredPointDefault).forEach(([event, handler]) => {
    map.on(event, "unclustered-point-default", handler);
  });

  usedIcons.forEach(iconName => {
    const listenerKey = `unclusteredPoint${iconName.charAt(0).toUpperCase() + iconName.slice(1).replace(/-/g, "")}`;
    Object.entries(listeners[listenerKey] || {}).forEach(([event, handler]) => {
      map.on(event, `unclustered-point-${iconName}`, handler);
    });
  });

  usedIconifyIds.forEach((iconifyId) => {
    const layerId = sanitizeIconifyIdForLayer(iconifyId);
    const listenerKey = `unclusteredPoint${layerId.charAt(0).toUpperCase() + layerId.slice(1).replace(/-/g, "")}`;
    if (listeners[listenerKey]) {
      Object.entries(listeners[listenerKey]).forEach(([event, handler]) => {
        map.on(event, `unclustered-point-${layerId}`, handler);
      });
    }
  });

  const poiLayerIds = [
    "clusters",
    "unclustered-point-default",
    ...usedIcons.map((n) => "unclustered-point-" + n),
    ...usedIconifyIds.map((id) => "unclustered-point-" + sanitizeIconifyIdForLayer(id)),
  ];
  map.getCanvas().addEventListener("touchend", function poiTouchEnd(e) {
    try {
      const point = getPointFromTouch(e);
      if (!point || typeof point.x !== "number" || typeof point.y !== "number") return;
      const px = Number(point.x);
      const py = Number(point.y);
      if (!Number.isFinite(px) || !Number.isFinite(py)) return;
      const features = map.queryRenderedFeatures({ x: px, y: py }, { layers: poiLayerIds });
      if (!features?.length) return;
      e.preventDefault();
      map._mapboxTouchHandledUntil = Date.now() + 400;
      const top = features[0];
      if (top.layer?.id === "clusters") {
        const clusterId = top.properties.cluster_id;
        const clusterSource = map.getSource("pois");
        clusterSource.getClusterLeaves(clusterId, Infinity, 0, (error, points) => {
          if (error) return;
          const coordinates = points.map((p) => p.geometry.coordinates);
          const bounds = coordinates.reduce(
            (bounds, coord) => bounds.extend(coord),
            new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
          );
          map.fitBounds(bounds, { padding: 100, maxZoom: 17 });
        });
      } else {
        const props = top.properties;
        if (onPoiClick && props) {
          onPoiClick(props);
        } else {
          const popup = getPopup(props, scenes, mapDisplayMode);
          if (popup) new PopupMap({ target: document.body, props: popup });
        }
      }
    } catch (_) {}
  }, { passive: false });

  map._poiListeners = listeners;
  map._poiSymbolIconIds = [...usedIcons, ...usedIconifyIds.map(sanitizeIconifyIdForLayer)];
}

const SYMBOL_POI_ICONS = ['accessibility-1', 'accessibility-2', 'accessibility-3', 'accessibility-4', 'pin'];

/**
 * Add a single hidden POI to the map temporarily (e.g. when selected from map menu).
 * Uses the POI's configured icon (same as visible POIs); falls back to default circle if none.
 */
export function addHiddenPoiToMap(map, poi, { bgColor, textColor, hoverColor: hoverColorProp }) {
  const initial = map._poiInitialGeojson;
  if (!initial || !map.getSource("pois")) return;
  let coordinates;
  if (Array.isArray(poi.location) && poi.location.length >= 2) {
    coordinates = poi.location;
  } else if (typeof poi.location === "string") {
    try {
      coordinates = JSON.parse(poi.location);
    } catch {
      return;
    }
  } else {
    return;
  }
  bgColor = bgColor || "#000000";
  const hoverColor = (hoverColorProp && hoverColorProp.trim()) ? hoverColorProp : getHoverColor(bgColor);
  const featureId = getPoiFeatureId(poi, 0);
  const isSymbol = poi.icon && SYMBOL_POI_ICONS.includes(poi.icon);
  const isIconify = poi.icon && String(poi.icon).includes(":");
  const effectiveBg = (poi.bgColor && poi.bgColor.trim()) ? poi.bgColor : bgColor;
  const iconImage = isSymbol
    ? (poi.bgColor ? `${poi.icon}-${poi.bgColor.replace("#", "")}-icon` : `${poi.icon}-icon`)
    : isIconify
      ? (effectiveBg === bgColor ? `${sanitizeIconifyIdForLayer(poi.icon)}-icon` : `${sanitizeIconifyIdForLayer(poi.icon)}-${effectiveBg.replace("#", "")}-icon`)
      : null;
  const hoverIconId = isSymbol
    ? `${poi.icon}-${hoverColor.replace("#", "")}-hover-icon`
    : isIconify
      ? `${sanitizeIconifyIdForLayer(poi.icon)}-${hoverColor.replace("#", "")}-hover-icon`
      : null;
  const newFeature = {
    type: "Feature",
    id: featureId,
    properties: {
      ...poi,
      id: featureId,
      icon: poi.icon || "default",
      bgColor: effectiveBg,
      hoverColor,
      ...((isSymbol || isIconify) && { iconImage, hoverIconId, currentIcon: iconImage }),
    },
    geometry: { type: "Point", coordinates },
  };
  const newData = {
    type: "FeatureCollection",
    features: [...initial.features, newFeature],
  };
  map.getSource("pois").setData(newData);
  map._poiHiddenPoiId = poi.id;
  map._poiHiddenFeature = newFeature;
}

/**
 * Remove the temporarily added hidden POI from the map and clear highlight.
 * Clear _poiHiddenFeature before clearPoiHighlight so applySymbolHighlight() won't re-append it.
 */
export function removeHiddenPoiFromMap(map) {
  if (map._poiHiddenPoiId != null && map._poiInitialGeojson && map.getSource("pois")) {
    map._poiHiddenPoiId = undefined;
    map._poiHiddenFeature = undefined;
    map.getSource("pois").setData(map._poiInitialGeojson);
    if (map.clearPoiHighlight) map.clearPoiHighlight();
  }
}

function parseNumericPoiLocation(poi) {
  if (!Array.isArray(poi.location) || poi.location.length < 2) return null;
  return poi.location;
}

function pointerOnMapCanvas(map, domEvent) {
  const rect = map.getCanvas().getBoundingClientRect();
  return { x: domEvent.clientX - rect.left, y: domEvent.clientY - rect.top };
}

function publishNumericPoiTooltip(map, setTooltip, title, domEvent, poi, markerElement) {
  if (!title || !setTooltip || !domEvent) return;
  const { x, y } = pointerOnMapCanvas(map, domEvent);
  setTooltip({
    x,
    y,
    text: title,
    offsetY: poi && markerElement
      ? getNumericPoiTooltipOffsetYFromMarker(poi, markerElement)
      : undefined,
  });
}

export async function addNumericPOIs({ map, pois, bgColor, textColor, hoverColor: hoverColorProp, setTooltip, scenes, onNumericPoiClick, mapDisplayMode }) {
  pois = pois.filter((poi) => !poi.hidden);
  bgColor = bgColor || "#000000";
  textColor = textColor || getBlackOrWhite(bgColor);
  const strokeColor = textColor;
  const defaultHoverColor = (hoverColorProp && hoverColorProp.trim()) ? hoverColorProp : getHoverColor(bgColor);

  const markers = [];
  let hoveredId = null;
  let selectedId = null;
  let numericTooltipTransitionStop = null;
  let lastNumericTooltipEvent = null;

  const applyHighlight = () => {
    for (const entry of markers) {
      const active = entry.id === hoveredId || entry.id === selectedId;
      applyNumericDomMarkerHover(entry.element, entry.poi, entry.theme, active);
    }
    const hidden = map._numericPoiHiddenEntry;
    if (hidden) {
      const active = hidden.id === hoveredId || hidden.id === selectedId;
      applyNumericDomMarkerHover(hidden.element, hidden.poi, hidden.theme, active);
    }
  };

  const handleSelect = (entry) => {
    const props = { ...entry.poi, number: entry.number };
    if (onNumericPoiClick) {
      onNumericPoiClick(props);
    } else {
      const popup = getPopup(props, scenes, mapDisplayMode);
      if (popup) new PopupMap({ target: document.body, props: popup });
    }
  };

  for (let index = 0; index < pois.length; index++) {
    const poi = pois[index];
    const coordinates = parseNumericPoiLocation(poi);
    if (!coordinates) continue;

    const number = index + 1;
    const effectiveHoverColor = (poi.hoverColor && poi.hoverColor.trim()) ? poi.hoverColor : defaultHoverColor;
    const baseBg = (poi.bgColor && poi.bgColor.trim()) ? poi.bgColor : bgColor;
    const theme = { baseBg, hoverBg: effectiveHoverColor };

    const { element, anchor } = createNumericMapboxMarkerElement(poi, {
      bgColor: baseBg,
      textColor,
      strokeColor,
      number,
    });

    const marker = new mapboxgl.Marker({ element, anchor })
      .setLngLat(coordinates)
      .addTo(map);

    const entry = {
      id: String(poi.id),
      marker,
      element,
      poi,
      theme,
      number,
    };

    const onActivate = (e) => {
      e.stopPropagation();
      map._mapboxTouchHandledUntil = Date.now() + 400;
      handleSelect(entry);
    };

    if (prefersTouchTap()) {
      element.addEventListener(
        "touchend",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          onActivate(e);
        },
        { passive: false },
      );
    } else {
      element.addEventListener("click", onActivate);
    }
    element.addEventListener("mouseenter", (e) => {
      map._nonBuildingTooltipActive = true;
      hoveredId = entry.id;
      applyHighlight();
      lastNumericTooltipEvent = e;
      numericTooltipTransitionStop?.();
      publishNumericPoiTooltip(map, setTooltip, poi.title, e, poi, element);
      if (numericMarkerHoverScale(poi) != null) {
        numericTooltipTransitionStop = runDuringNumericMarkerScaleTransition(element, () => {
          publishNumericPoiTooltip(
            map,
            setTooltip,
            poi.title,
            lastNumericTooltipEvent,
            poi,
            element,
          );
        });
      }
      map.getCanvas().style.cursor = "pointer";
    });
    element.addEventListener("mousemove", (e) => {
      if (hoveredId !== entry.id) return;
      lastNumericTooltipEvent = e;
      publishNumericPoiTooltip(map, setTooltip, poi.title, e, poi, element);
    });
    element.addEventListener("mouseleave", () => {
      if (hoveredId === entry.id) hoveredId = null;
      numericTooltipTransitionStop?.();
      numericTooltipTransitionStop = null;
      lastNumericTooltipEvent = null;
      map._nonBuildingTooltipActive = false;
      applyHighlight();
      if (setTooltip) setTooltip(null);
      map.getCanvas().style.cursor = "";
    });

    markers.push(entry);
  }

  map._numericPoiMarkers = markers;
  map._numericPoiMarkerDefaults = { bgColor, textColor, strokeColor, defaultHoverColor };

  map.highlightNumericPoi = function (poiId) {
    selectedId = poiId ? String(poiId) : null;
    applyHighlight();
  };

  map.clearNumericPoiHighlight = function () {
    selectedId = null;
    applyHighlight();
  };
}

/**
 * Add a single hidden numeric POI to the map temporarily (e.g. when selected from map menu).
 */
export async function addHiddenNumericPoiToMap(map, poi, index, { bgColor, textColor, hoverColor: hoverColorProp }) {
  if (!map._numericPoiMarkers) return;
  removeHiddenNumericPoiFromMap(map);
  if (!Array.isArray(poi.location) || poi.location.length < 2) return;

  const defaults = map._numericPoiMarkerDefaults || {};
  bgColor = bgColor || defaults.bgColor || "#000000";
  textColor = textColor || defaults.textColor || getBlackOrWhite(bgColor);
  const strokeColor = textColor;
  const defaultHoverColor = (hoverColorProp && hoverColorProp.trim())
    ? hoverColorProp
    : (defaults.defaultHoverColor || getHoverColor(bgColor));
  const number = typeof index === "number" ? index + 1 : (map._numericPoiMarkers.length + 1);
  const effectiveHoverColor = (poi.hoverColor && poi.hoverColor.trim()) ? poi.hoverColor : defaultHoverColor;
  const baseBg = (poi.bgColor && poi.bgColor.trim()) ? poi.bgColor : bgColor;
  const theme = { baseBg, hoverBg: effectiveHoverColor };

  const { element, anchor } = createNumericMapboxMarkerElement(poi, {
    bgColor: baseBg,
    textColor,
    strokeColor,
    number,
  });

  const marker = new mapboxgl.Marker({ element, anchor })
    .setLngLat(poi.location)
    .addTo(map);

  map._numericPoiHiddenEntry = {
    id: String(poi.id),
    marker,
    element,
    poi,
    theme,
    number,
  };
  map._numericPoiHiddenId = poi.id;
  map.highlightNumericPoi?.(poi.id);
}

/**
 * Remove the temporarily added hidden numeric POI from the map and clear highlight.
 */
export function removeHiddenNumericPoiFromMap(map) {
  if (map._numericPoiHiddenEntry) {
    map._numericPoiHiddenEntry.marker.remove();
    delete map._numericPoiHiddenEntry;
  }
  if (map._numericPoiHiddenId != null) {
    if (map.clearNumericPoiHighlight) map.clearNumericPoiHighlight();
    map._numericPoiHiddenId = undefined;
  }
}

export function removeNumericPois({ map }) {
  const markers = map._numericPoiMarkers;
  if (markers) markers.forEach((entry) => entry.marker.remove());
  delete map._numericPoiMarkers;
  delete map._numericPoiMarkerDefaults;
  removeHiddenNumericPoiFromMap(map);
  delete map.highlightNumericPoi;
  delete map.clearNumericPoiHighlight;
}

